/**
 * The referee and the two linesmen — the three men on the pitch who are not players.
 *
 * A port of `reference/GameplayFootball/src/onthepitch/officials.cpp`,
 * `player/playerofficial.cpp` and `player/controller/refereecontroller.cpp` — about 380 lines
 * between them. Each official is an ORDINARY animated body: same skeleton, same clip library,
 * same movement solver as the twenty-two players (`src/sim/anim/runtime.ts`), driven by the
 * small controller below, which only ever asks for a plain "movement" command. The referee
 * drifts on a force field — pulled toward where the ball has recently been, pushed off the
 * ball's immediate future and off every player — and each linesman walks his own side's
 * offside line, 0.8 m outside the touchline, looking across the pitch.
 *
 * THREE THINGS THE ORIGINAL DOES THAT ARE DELIBERATELY NOT PORTED HERE:
 *
 *  1. `Match::Process` stretches a restart's timing whenever the referee's current animation
 *     is `special` (`AlterSetPiecePrepareTime`, `match.cpp:1040`), which would make how long a
 *     free kick waits depend on how far his body happened to be from the offender — and every
 *     baseline this project has already recorded would stop being comparable. Nothing here
 *     calls it, and that stays true now that the card ceremony itself is ported below
 *     (`showCardCommands`): the ten-second window it plays out in is fixed by
 *     `Referee.schedule` before he ever sets off toward the offender, and nothing here
 *     stretches it if he arrives late, or not at all.
 *  2. The original never runs an official through `Match::CheckBallCollisions` or
 *     `CheckHumanoidCollisions` (`match.cpp`) — `Officials::GetPlayers` feeds a separate list
 *     used only for rendering (`Match::GetOfficialPlayers`, `match.cpp:631-633`), never the
 *     one collision walks. Reproduced here by construction, not by a guard: an official is a
 *     real `MatchPlayer`, but is never pushed into a `TeamState`'s own roster, and every
 *     collision pass in this port walks `match.players`, which is built from exactly that
 *     roster (`src/sim/collision.ts:50`, `src/sim/match.ts:500-502`).
 *  3. Likewise never in `Team::GetActivePlayers`, so never in the fidelity trace's twenty-two
 *     roster places either (`src/sim/trace.ts:199-210`, which hard-requires exactly 22 and
 *     throws otherwise). An official kept out of both team rosters is automatically kept out
 *     of the trace too, with nothing extra to maintain.
 *
 * STILL OUT OF SCOPE: the card mesh itself, parented to the referee's hand during the
 * show-card animation in the original (`officials.cpp:97-118`). The on-screen HUD banner
 * already names the card the instant it is given (`CardWatcher`, `src/ui/scoreboard.ts`), so
 * what was missing here was purely his body acting it out — walking to the offender and
 * playing the gesture — which `showCardCommands` below now does. The "walk to the offender and
 * hold a card up" branch of `RefereeController::RequestCommand` (`refereecontroller.cpp:63-99`)
 * is that branch; `refereeCommand` below reaches for it whenever a card was shown for the
 * restart currently under way, and falls back to the ordinary NORMAL branch
 * (`refereecontroller.cpp:101-117`) the rest of the time.
 */

import { Vector3 } from 'three';
import { PITCH } from '../core/constants';
import { clamp, normalizedOr, normalizedOrZero } from '../core/math';
import { DRIBBLE_VELOCITY, IDLE_VELOCITY, SPRINT_VELOCITY, WALK_VELOCITY, rangeVelocity } from './anim/velocity';
import type { PlayerCommand } from './anim/select';
import { HumanoidAnim } from './anim/runtime';
import type { PlayerPhysics } from './anim/physics';
import type { AnimClip } from './anim/clip';
import type { Skeleton } from './skeleton';
import { Match, MatchPlayer } from './match';
import { offsideLine } from './teamai';

/** gamedefines.hpp:54 — the universal "how fast should I run to close this gap" gain. */
const DISTANCE_TO_VELOCITY = 2.6;

/** officials.cpp:37-39 — fixed for the whole match; the original never re-teleports them. */
export const REFEREE_START_POSITION = new Vector3(10, -10, 0);
export const LINESMAN_A_START_POSITION = new Vector3(25, -36.5, 0);
export const LINESMAN_B_START_POSITION = new Vector3(-25, 36.5, 0);

/** refereecontroller.cpp:159-161 — flat for both roles; nothing scales it by a stat. */
export const OFFICIAL_REACTION_TIME_MS = 60;

/** officials.cpp:41,145-147 — the shirt every one of the three wears. Already ships; see report. */
export const OFFICIAL_KIT_TEXTURE = 'media/objects/players/textures/referee_kit.png';

export type OfficialKind = 'referee' | 'linesman';

/**
 * One official's simulation half: the match sees him exactly like a player, minus the roster
 * membership that would pull him into collision, team AI and the trace. See the file header.
 */
export interface Official {
  readonly kind: OfficialKind;
  readonly player: MatchPlayer;
  readonly anim: HumanoidAnim;
  readonly skeleton: Skeleton;
}

/**
 * `PlayerBase::GetStat` (`playerbase.cpp:127-129`) reads straight off `PlayerData`, with none
 * of the difficulty/fatigue modifier `Player::GetStat` applies — officials are `PlayerBase`,
 * not `Player`. The original hands the referee AND both linesmen the very same, freshly
 * constructed `PlayerData` (`officials.cpp:26-29`), which has no stat loaded into it, so every
 * read falls through to `Properties::GetReal`'s own default of 1.0 (`playerdata.cpp:110-114`,
 * `Properties::GetReal`). Officials therefore move at the best physical numbers in the game,
 * flat, for as long as the match lasts — nothing ever tires them, because they are never in
 * `Match.allPlayers` and so never drained (`src/sim/match.ts` `finishStep`).
 */
function officialPhysics(): PlayerPhysics {
  return {
    maxVelocity: SPRINT_VELOCITY,
    fatigueFactorInv: 1,
    stat: () => 1,
    lastTouchBias: () => 0,
    decayingPositionOffset: new Vector3(),
  };
}

export interface CreateOfficialOptions {
  id: number;
  library: AnimClip[];
  position: Vector3;
  skeleton: Skeleton;
  /** A stream of this official's own, never the platform generator. See the caller in main.ts. */
  random: () => number;
  /** 0..1, which frame of his idle clip he starts on, so three officials don't breathe in step. */
  idleFrameBias?: number;
}

/**
 * Build one official's `MatchPlayer` + animation runtime — `PlayerOfficial::Activate`
 * (`playerofficial.cpp:28-36`) plus `HumanoidBase::ResetPosition(pos, Vector3(0))`
 * (`officials.cpp:37-39`): every official starts out looking at the centre spot.
 *
 * Deliberately NOT added to any `TeamState`; see the file header for why. `teamId` is set to 0
 * only because `MatchPlayer` requires one and a handful of read-only lookups inside the
 * animation runtime dereference it (`match.team(self.teamId)`, always false comparisons for an
 * official) — nothing about an official's own behaviour depends on it.
 */
export function createOfficial(kind: OfficialKind, options: CreateOfficialOptions): Official {
  const physics = officialPhysics();
  const anim = new HumanoidAnim({
    library: options.library,
    player: physics,
    position: options.position.clone(),
    focus: new Vector3(0, 0, 0),
    idleFrameBias: options.idleFrameBias ?? 0,
    random: options.random,
  });
  const player = new MatchPlayer(options.id, 0, anim, physics, OFFICIAL_REACTION_TIME_MS);
  return { kind, player, anim, skeleton: options.skeleton };
}

/**
 * `AI_GetForceFieldMovement` (`AIfunctions.cpp:475-514`) — blend every pole in a force field
 * into one desired-movement vector, metres per second.
 *
 * A repeller's push is never damped, however close its origin is; an attractor's pull is
 * damped inside `attractorDampingDistance` so the referee eases up as he nears wherever the
 * ball has been rather than overshooting it. Every pole the referee ever uses has a constant
 * exponent of 1 (`refereecontroller.cpp:24-56` sets none), so — unlike the general AI version
 * this deliberately mirrors in shape — there is no exponent term to carry.
 */
function forceFieldMovement(field: ForceSpot[], from: Vector3, attractorDampingDistance = 10): Vector3 {
  const cumulative = new Vector3();
  let totalForce = 0;
  for (const spot of field) {
    const delta = spot.origin.clone().sub(from);
    const distance = delta.length();
    const intensity = Number.isFinite(spot.scale) ? clamp(1 - distance / spot.scale, 0, 1) : 1;
    if (intensity <= 0) continue;
    const direction = normalizedOrZero(delta);
    if (spot.repel) direction.negate();
    else if (distance < attractorDampingDistance) direction.multiplyScalar(distance / attractorDampingDistance);
    const force = spot.power * intensity;
    cumulative.addScaledVector(direction, force);
    totalForce += force;
  }
  if (totalForce === 0) return cumulative;
  return cumulative.divideScalar(totalForce).multiplyScalar(SPRINT_VELOCITY);
}

interface ForceSpot {
  origin: Vector3;
  repel: boolean;
  /** `Infinity` reproduces `e_DecayType_Constant` — distance never weakens the pole. */
  scale: number;
  power: number;
}

/**
 * `RefereeController::GetForceField` (`refereecontroller.cpp:24-56`) — the referee's whole
 * world, boiled down to three kinds of pole: drawn toward where the ball has recently been,
 * pushed off where it is about to go, and pushed off every man on the pitch.
 */
function refereeForceField(match: Match): ForceSpot[] {
  const field: ForceSpot[] = [
    {
      // 0.6 of the way to the ball's own 2-second average position — never the ball itself,
      // so he does not chase every touch, only drifts toward where the game has been.
      origin: match.ball.averagePosition(2000).setZ(0).multiplyScalar(0.6),
      repel: false,
      scale: Infinity,
      power: 0.5,
    },
    {
      // Pushed off 200 ms of where the ball is about to be, so he backs away from a ball
      // arriving at him rather than standing in its path.
      origin: match.ball.predict(200).setZ(0),
      repel: true,
      scale: 10,
      power: 0.5,
    },
  ];

  // Pushed off every active player, a touch ahead of where he is heading — refereecontroller
  // asks for both teams' active players and gives each the same weight (`:44-55`).
  for (const team of match.teams) {
    for (const player of team.players) {
      field.push({
        origin: player.position.clone().addScaledVector(player.movement, 0.4),
        repel: true,
        scale: 10,
        power: 0.5,
      });
    }
  }

  return field;
}

/**
 * `specialvar1` 3 — the numbering `controller.ts`'s own `CELEBRATION_*` comment lays out in
 * full: 1 and 2 are the two celebrations, 4 and 5 the punch and the flying kick. The clip is
 * `special/000_showcard` (library index 1332): a raised-arm hold whose root track translates
 * by fractions of a centimetre over the whole of its 149 frames, so — like the punch, and
 * unlike the flying kick — it was drawn from a standstill, and the hard filter will only start
 * it on a referee who has actually stopped (`crudeSelection`'s incoming-speed bucket,
 * `select.ts`).
 *
 * IT CARRIES NO `specialvar2` AT ALL — confirmed straight off the source clip
 * (`reference/GameplayFootball/data/media/animations/special/000_showcard.anim`, which has no
 * `<specialvar2>` block) and off the converted library, whose entry for this clip has no
 * `specialvar2` key either. A command that named one could never reach it — see CLAUDE.md's
 * "Eight clips in every player's download are not football" entry. The fix is exactly what
 * `flyingKickCommands` and `punchCommands` already do in `controller.ts`: name only
 * `specialVar1` below and leave `specialVar2` unset, so the hard filter's `parseFloatOr0` on
 * the query's absent property reads 0 and matches the clip's own absent variable, also 0
 * (`select.ts:456-457`).
 */
const SPECIAL_SHOWCARD = 3;

/** How close the referee plants himself before he will raise the card. */
const SHOWCARD_STANDOFF_M = 1.5;

/**
 * Walk to the offender and hold the card up — `RefereeController::RequestCommand`'s FOUL
 * branch (`refereecontroller.cpp:63-99`), the one branch of it this port had not carried over;
 * see the file header.
 *
 * Two commands, exactly the shape `punchCommands` already uses in `controller.ts` for the same
 * reason: the special clip first, asking for a dead stop, so its own hard filter can only
 * admit it once he has actually arrived and slowed all the way to idle; a plain walk behind
 * it, aimed at the offender and decelerating as he closes, so that tick eventually comes.
 * Neither command can steer the clip itself once it is playing — a special clip's physics bias
 * is zero, so it plays exactly as drawn from whatever facing he happens to arrive with — which
 * is why the approach is a walk rather than a snap: by the time the movement command has
 * braked him to a stop in front of a man, it has also had the ticks it needed to turn him to
 * face that man.
 */
function showCardCommands(referee: MatchPlayer, offender: MatchPlayer): PlayerCommand[] {
  const toOffender = offender.position.clone().sub(referee.position).setZ(0);
  const distance = toOffender.length();
  const direction = normalizedOr(toOffender, referee.directionVec);
  // "Take it easy, we are the ref" again — a walk, not the sprint `chaseCommand` allows an AI
  // player closing on the ball (`controller.ts`), easing down through a dribble-paced band
  // exactly as that same helper does, just capped lower.
  let velocity = WALK_VELOCITY;
  if (distance < SHOWCARD_STANDOFF_M) velocity = IDLE_VELOCITY;
  else if (distance < SHOWCARD_STANDOFF_M + 3) velocity = DRIBBLE_VELOCITY;
  const lookAt = offender.position.clone().setZ(0);
  return [
    {
      // The one `special` type, exactly the celebration's and the punch's own reasoning: a
      // trace records the original's own function numbering, gaps and all, and this stays out
      // of it (`controller.ts`).
      desiredFunctionType: 'special',
      useDesiredMovement: false,
      desiredDirection: direction.clone(),
      desiredVelocityFloat: IDLE_VELOCITY,
      useDesiredLookAt: false,
      desiredLookAt: lookAt.clone(),
      specialVar1: SPECIAL_SHOWCARD,
    },
    {
      desiredFunctionType: 'movement',
      useDesiredMovement: true,
      desiredDirection: direction,
      desiredVelocityFloat: velocity,
      useDesiredLookAt: true,
      desiredLookAt: lookAt,
    },
  ];
}

/**
 * The referee's whole command: walk over and show a card he just gave, or — the rest of the
 * time — the ordinary drift, `RefereeController::RequestCommand`'s NORMAL branch
 * (`refereecontroller.cpp:101-117`).
 */
export function refereeCommand(match: Match, referee: MatchPlayer): PlayerCommand[] {
  const shown = match.referee.cardShown;
  const restart = match.referee.restart;
  // The window this stoppage's own card is worth walking over for: from the tick it was shown
  // — always the same tick `Referee.schedule` created this restart — until the restart is
  // prepared and everybody, offender included, is teleported onto a fresh mark. Bounded by the
  // restart itself rather than by a duration of its own, so this needs no constant to keep in
  // step with the ten-second ceremony `schedule` already pays for, and a stale card from some
  // earlier stoppage can never be re-shown by accident.
  if (shown && restart && !restart.prepared && shown.atMs === restart.stopTimeMs) {
    return showCardCommands(referee, shown.player);
  }

  const desiredPosition = referee.position
    .clone()
    .add(forceFieldMovement(refereeForceField(match), referee.position));
  const toDesired = desiredPosition.sub(referee.position);
  const direction = normalizedOr(toDesired, referee.directionVec);
  // "take it easy, we are the ref" (refereecontroller.cpp:113) — half the usual gain, and a
  // continuous clamp rather than the linesman's quantised RangeVelocity.
  const velocity = clamp(toDesired.length() * DISTANCE_TO_VELOCITY * 0.5, IDLE_VELOCITY, SPRINT_VELOCITY);
  return [
    {
      desiredFunctionType: 'movement',
      useDesiredMovement: true,
      desiredDirection: direction,
      desiredVelocityFloat: velocity,
      useDesiredLookAt: true,
      desiredLookAt: match.ball.predict(60).setZ(0),
    },
  ];
}

/**
 * A linesman's whole job: stand on his own side's offside line, 0.8 m outside the touchline,
 * looking square across the pitch — `RefereeController::RequestCommand`'s linesman branch
 * (`refereecontroller.cpp:120-143`).
 *
 * Which line he tracks answers to which touchline he is CURRENTLY standing on, not to which
 * linesman he is — the original tests `player->GetPosition().coords[1] < 0` fresh every tick,
 * not an identity assigned at construction. Since he starts on a touchline and this command
 * only ever asks him to walk along it, the two never diverge in practice.
 */
export function linesmanCommand(match: Match, linesman: MatchPlayer): PlayerCommand[] {
  const onSouthTouchline = linesman.position.y < 0;
  const defendingTeamId = onSouthTouchline ? 1 : 0;
  const offsideX = offsideLine(match, defendingTeamId, match.mentalImage(0), 0);
  const y = onSouthTouchline ? -(PITCH.halfWidth + 0.8) : PITCH.halfWidth + 0.8;
  const desiredPosition = new Vector3(offsideX, y, 0);

  const toDesired = desiredPosition.clone().sub(linesman.position);
  const direction = normalizedOr(toDesired, linesman.directionVec);
  const velocity = rangeVelocity(toDesired.length() * DISTANCE_TO_VELOCITY);

  return [
    {
      desiredFunctionType: 'movement',
      useDesiredMovement: true,
      desiredDirection: direction,
      desiredVelocityFloat: velocity,
      useDesiredLookAt: true,
      desiredLookAt: new Vector3(desiredPosition.x, 0, 0),
    },
  ];
}

function officialCommand(match: Match, official: Official): PlayerCommand[] {
  return official.kind === 'referee'
    ? refereeCommand(match, official.player)
    : linesmanCommand(match, official.player);
}

/**
 * One official's tick: animate, then pose — the same two steps `stepMatch` runs for a real
 * player (`src/sim/steploop.ts:60-64`), and nothing else. No collision, no fatigue, no trace:
 * an official never reaches `Match.finishStep`, because nothing here ever calls it on his
 * behalf and he is not in the list `finishStep` itself walks.
 *
 * THE ONE THING HE NOW FEEDS BACK INTO THE FOOTBALL is where he ended up standing and which
 * way he is looking. The rules referee owns no position of its own — it is a set of rules —
 * so it is told, here, once a tick, and what it does with it is decide whether an offence far
 * away from him was seen at all (`sawOffence`, `src/sim/referee.ts`). Nothing else about an
 * official has ever touched the simulation and nothing else does now.
 */
export function tickOfficial(match: Match, official: Official): void {
  official.anim.tick(match, official.player, () => officialCommand(match, official));
  official.anim.writePose(official.skeleton);
  official.skeleton.update();
  match.referee.observeOfficial(
    official.player.id,
    official.kind,
    official.player.position,
    official.player.directionVec,
  );
}

export function tickOfficials(match: Match, officials: readonly Official[]): void {
  for (const official of officials) tickOfficial(match, official);
}
